import React, { useEffect, useState } from 'react';
import axios from 'axios'; // Import axios for API calls
import BaseEdit from 'components/Base/BaseEdit'; // Import BaseEdit for editing fields
import BaseModal from 'components/Base/BaseModal';
import { parseError } from 'utils/helpers'; // Import error parsing utility
import {
    CalendarIcon,
    BanknotesIcon,
    ChevronDownIcon,
    ClockIcon,
    DocumentTextIcon,
    CheckCircleIcon,
    ClockIcon as ClockIconSolid,
    UserPlusIcon,
    UserGroupIcon,
    UserCircleIcon,
    XMarkIcon,
    ExclamationTriangleIcon,
    ChevronRightIcon,
} from "@heroicons/react/24/outline";
import moment from 'moment';
import { Link } from 'react-router-dom';

const TabPartner = () => {
    const [allJobs, setAllJobs] = useState([]);
    const [monthlyJobs, setMonthlyJobs] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(moment().format('YYYY-MM'));
    const [error, setError] = useState({}); // State for error handling
    const [saved, setSaved] = useState(false); // State to track if changes are saved
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [availableMonths, setAvailableMonths] = useState([]);
    const [newUser, setNewUser] = useState({ email: '', password: '' });
    const [addUserStatus, setAddUserStatus] = useState({ success: false, message: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [partnerUsers, setPartnerUsers] = useState([]);
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, email: null });
    const [expandedInvoice, setExpandedInvoice] = useState(null);

    // Fetch all jobs and process them
    useEffect(() => {
        axios.get('/jobs/my')
            .then(({ data }) => {
                const jobs = data || [];
                setAllJobs(jobs);

                // Process jobs to get available months
                const months = [...new Set(jobs.map(job =>
                    moment(job.date[0]).format('YYYY-MM')
                ))].sort().reverse();

                setAvailableMonths(months);
            })
            .catch((error) => {
                console.error('Error fetching jobs:', error);
            });
    }, []);

    useEffect(() => {
        if (allJobs.length > 0) {
            const jobsForMonth = allJobs.filter(job =>
                moment(job.date[0]).format('YYYY-MM') === selectedMonth &&
                (job.status === 'completed' || job.status === 'working')
            );

            setMonthlyJobs(jobsForMonth);
        }
    }, [selectedMonth, allJobs]);

    useEffect(() => {
        axios.get('/profile/invoice')
            .then(({ data }) => {
                setInvoices(data.invoices || []);
            })
            .catch((error) => {
                console.error('Error fetching invoices:', error);
            });
    }, []);

    const getCurrentMonthTotal = () => {
        const currentMonth = moment().format('YYYY-MM');
        const currentInvoice = invoices.find(invoice =>
            moment(invoice.month).format('YYYY-MM') === currentMonth
        );
        return currentInvoice ? parseFloat(currentInvoice.totalAmount) : 0;
    };

    useEffect(() => {
        const fetchPartnerUsers = async () => {
            try {
                const { data } = await axios.get('/profile/partner/list');
                setPartnerUsers(data);
            } catch (err) {
                console.error('Error fetching partner users:', err);
            }
        };

        fetchPartnerUsers();
    }, [addUserStatus.success]);

    const handleAddUser = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError({});
        setAddUserStatus({ success: false, message: '' });

        try {
            const response = await axios.post('/auth/partner/add-user', newUser);
            setAddUserStatus({ success: true, message: 'User added successfully!' });
            setNewUser({ email: '', password: '' });
        } catch (err) {
            const errorMessage = parseError(err);
            setError({ addUser: errorMessage });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRemoveUser = async (email) => {
        setConfirmModal({ isOpen: true, email });
    };

    const handleConfirmRemove = async () => {
        try {
            await axios.delete(`/profile/partner/user/${confirmModal.email}`);
            setPartnerUsers(prevUsers => prevUsers.filter(user => user.email !== confirmModal.email));
            setConfirmModal({ isOpen: false, email: null });
        } catch (err) {
            const errorMessage = parseError(err);
            setError({ removeUser: errorMessage });
        }
    };

    const toggleInvoice = (invoice) => {
        setExpandedInvoice(expandedInvoice === invoice._id ? null : invoice._id);
    };

    return (
        <div className="min-h-screen">
            <div className="mx-auto pt-8" style={{ maxWidth: '1100px' }}>
                {/* Current Month Summary Card */}
                <div className="bg-white rounded-2xl p-6 shadow-lg shadow-sky-900/5 ring-1 ring-slate-100 mb-6">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                            <BanknotesIcon className="h-5 w-5 text-sky-500 mr-2" />
                            Current Month Charges
                        </h2>
                        <div className="text-2xl font-bold text-gray-900">
                            ${getCurrentMonthTotal().toFixed(2)}
                        </div>
                    </div>
                    <div className="mt-4 border-t border-gray-100 pt-4">
                        <div className="text-sm text-gray-600 space-y-2">
                            <p className="font-medium text-gray-700">Invoice Calculation:</p>
                            <div className="grid grid-cols-1 gap-1.5">
                                <div className="flex items-center">
                                    <span className="w-2 h-2 rounded-full bg-sky-500 mr-2"></span>
                                    <span>Base job price</span>
                                </div>
                                <div className="flex items-center">
                                    <span className="w-2 h-2 rounded-full bg-sky-500 mr-2"></span>
                                    <span> +20% Emissary service fee</span>
                                </div>
                                <div className="flex items-center">
                                    <span className="w-2 h-2 rounded-full bg-sky-500 mr-2"></span>
                                    <span>+$0.25 PayPal processing fee per transaction</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Monthly Invoices Section */}
                <div className="bg-white rounded-2xl p-6 shadow-lg shadow-sky-900/5 ring-1 ring-slate-100 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center mb-4">
                        <DocumentTextIcon className="h-5 w-5 text-sky-500 mr-2" />
                        Monthly Invoices
                    </h2>
                    <div className="space-y-4">
                        {invoices.map((invoice, index) => (
                            <div key={index} className="border rounded-lg overflow-hidden">
                                <div
                                    className="p-4 hover:bg-gray-50 cursor-pointer"
                                    onClick={() => toggleInvoice(invoice)}
                                >
                                    <div className="flex justify-between items-center">
                                        <div className="flex items-center">
                                            <ChevronRightIcon
                                                className={`h-5 w-5 text-gray-400 mr-2 transition-transform ${expandedInvoice === invoice._id ? 'transform rotate-90' : ''
                                                    }`}
                                            />
                                            <div className="text-lg font-medium text-gray-900">
                                                {moment(invoice.month).format('MMMM YYYY')}
                                            </div>
                                        </div>
                                        <div className="text-lg font-semibold text-gray-900">
                                            ${parseFloat(invoice.totalAmount).toFixed(2)}
                                        </div>
                                    </div>
                                    <div className="mt-2 text-sm text-gray-500">
                                        {invoice.jobs.length} jobs
                                    </div>
                                </div>

                                {expandedInvoice === invoice._id && (
                                    <div className="border-t bg-gray-50 p-4">
                                        <div className="space-y-3">
                                            {invoice.jobs.map((job) => (
                                                <Link
                                                    key={job._id}
                                                    to={`/jobs/${job._id}`}
                                                    className="block p-3 bg-white rounded-lg hover:bg-gray-50 transition-colors duration-150"
                                                >
                                                    <div className="flex justify-between items-start">
                                                        <div>
                                                            <h4 className="font-medium text-gray-900">{job.title}</h4>
                                                            <p className="text-sm text-gray-500">
                                                                Patient: {job.patient?.name} {job.patient?.surname}
                                                            </p>
                                                            <div className="mt-1 flex items-center text-sm text-gray-500">
                                                                <ClockIcon className="h-4 w-4 mr-1" />
                                                                {moment(job.date[0]).format('MMM D, YYYY')}
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col items-end">
                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                ${parseFloat(job.price).toFixed(2)}
                                                            </span>
                                                            <span className={`mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${job.status === 'completed'
                                                                    ? 'bg-green-100 text-green-800'
                                                                    : 'bg-blue-100 text-blue-800'
                                                                }`}>
                                                                {job.status === 'completed' ? (
                                                                    <CheckCircleIcon className="h-4 w-4 mr-1" />
                                                                ) : (
                                                                    <ClockIconSolid className="h-4 w-4 mr-1" />
                                                                )}
                                                                {job.status === 'completed' ? 'Completed' : 'In Progress'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Partner Users List Section */}
                <div className="bg-white rounded-2xl p-6 shadow-lg shadow-sky-900/5 ring-1 ring-slate-100 mb-6">
                    <div className="flex items-center mb-6">
                        <UserGroupIcon className="h-5 w-5 text-sky-500 mr-2" />
                        <h2 className="text-lg font-semibold text-gray-900">Partner Users</h2>
                    </div>

                    <div className="space-y-4">
                        {error.removeUser && (
                            <div className="text-sm text-red-600 mb-4">
                                {error.removeUser}
                            </div>
                        )}

                        {partnerUsers.map((user, index) => (
                            <div
                                key={index}
                                className="flex items-center justify-between p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors duration-150"
                            >
                                <div className="flex items-center space-x-3">
                                    <UserCircleIcon className="h-6 w-6 text-gray-400" />
                                    <span className="text-gray-900">{user.email}</span>
                                    {user.isMainAccount && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-800">
                                            Main Account
                                        </span>
                                    )}
                                </div>
                                {!user.isMainAccount && (
                                    <button
                                        onClick={() => handleRemoveUser(user.email)}
                                        className="p-1 text-gray-400 hover:text-red-500 transition-colors duration-150"
                                        title="Remove user"
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Monthly Jobs Section */}
                <div className="bg-white rounded-2xl p-6 shadow-lg shadow-sky-900/5 ring-1 ring-slate-100">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                            <CalendarIcon className="h-5 w-5 text-sky-500 mr-2" />
                            Monthly Jobs
                        </h2>
                        <div className="relative">
                            <button
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                className="inline-flex items-center justify-between px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                            >
                                {moment(selectedMonth).format('MMMM YYYY')}
                                <ChevronDownIcon className="h-4 w-4 ml-2" />
                            </button>

                            {isDropdownOpen && (
                                <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                                    <div className="py-1" role="menu">
                                        {availableMonths.map((month) => (
                                            <button
                                                key={month}
                                                onClick={() => {
                                                    setSelectedMonth(month);
                                                    setIsDropdownOpen(false);
                                                }}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                            >
                                                {moment(month).format('MMMM YYYY')}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Jobs Grid */}
                    {monthlyJobs.length === 0 ? (
                        <div className="text-center py-12">
                            <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900">No jobs for this month</h3>
                            <p className="mt-1 text-sm text-gray-500">There are no completed or working jobs for the selected month.</p>
                        </div>
                    ) : (
                        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                            {monthlyJobs.map((job) => (
                                <div
                                    key={job._id}
                                    className="group relative flex flex-col rounded-xl bg-gray-50 p-5 transition-all duration-200"
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h3 className="text-base font-semibold text-gray-900">
                                                {job.title}
                                            </h3>
                                            <p className="text-sm text-blue-600">
                                                Patient: {job.patient.name} {job.patient.surname}
                                            </p>
                                        </div>
                                        <div className="flex flex-col items-end gap-2">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                ${(job.price).toFixed(2)}
                                            </span>
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${job.status === 'completed'
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-blue-100 text-blue-800'
                                                }`}>
                                                {job.status === 'completed' ? (
                                                    <CheckCircleIcon className="h-4 w-4 mr-1" />
                                                ) : (
                                                    <ClockIconSolid className="h-4 w-4 mr-1" />
                                                )}
                                                {job.status === 'completed' ? 'Completed' : 'In Progress'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="mt-4">
                                        <div className="flex items-center text-sm text-gray-500">
                                            <ClockIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                                            {moment(job.date[0]).format('MMM D, YYYY')}
                                            {job.date.length > 1 && (
                                                <span> - {moment(job.date[1]).format('MMM D, YYYY')}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Add New Partner User Section */}
                <div className="bg-white rounded-2xl p-6 mt-6 shadow-lg shadow-sky-900/5 ring-1 ring-slate-100 mb-6">
                    <div className="flex items-center mb-6">
                        <UserPlusIcon className="h-5 w-5 text-sky-500 mr-2" />
                        <h2 className="text-lg font-semibold text-gray-900">Add Partner User</h2>
                    </div>

                    <form onSubmit={handleAddUser} className="space-y-4">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    value={newUser.email}
                                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                                    required
                                />
                            </div>
                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                    Password
                                </label>
                                <input
                                    type="password"
                                    id="password"
                                    value={newUser.password}
                                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                                    required
                                />
                            </div>
                        </div>

                        {error.addUser && (
                            <div className="text-sm text-red-600 mt-2">
                                {error.addUser}
                            </div>
                        )}

                        {addUserStatus.success && (
                            <div className="text-sm text-green-600 mt-2">
                                {addUserStatus.message}
                            </div>
                        )}

                        <div className="flex justify-end">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
                            >
                                {isSubmitting ? 'Adding...' : 'Add User'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <BaseModal
                show={confirmModal.isOpen}
                onClose={() => setConfirmModal({ isOpen: false, email: null })}
                width={400}
            >
                <div>
                    <div className="flex items-start">
                        <div className="flex-shrink-0">
                            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                        </div>
                        <div className="ml-3">
                            <h3 className="text-lg font-medium text-gray-900">
                                Remove User
                            </h3>
                            <div className="mt-2">
                                <p className="text-sm text-gray-500">
                                    Are you sure you want to remove {confirmModal.email}? This action cannot be undone.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="mt-6 flex justify-end space-x-3">
                        <button
                            type="button"
                            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            onClick={() => setConfirmModal({ isOpen: false, email: null })}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
                            onClick={handleConfirmRemove}
                        >
                            Remove
                        </button>
                    </div>
                </div>
            </BaseModal>
        </div>
    );
};

export default TabPartner;